﻿import React, { Fragment, useState, useContext, useReducer, useEffect } from 'react';
import { groupBy, initial } from 'lodash';
import moment from "moment";

import { BookingFlowContext } from '../BookingFlowContext'
import { usePrevious } from '../hooks/usePrevious'
import { useProductAvailability } from '../hooks/useProductAvailability'
import { validateFormObjectAlt as validateFormObject } from '../Validation'
import { useAccessDeviceAvailability, calculateLeastRequiredOrBundledADAvailable } from '../hooks/useAccessDeviceAvailability'
import { parseDate } from '../../../../../Resources/Scripts/_Shared/parseDate'
import { HeadingAndText } from '../partials/HeadingAndText'
import { Error } from '../../../../../Resources/Scripts/_Shared/elements/Error'
import { DateAndProductSelection } from '../partials/DateAndProductSelection'
import { SpotSelection } from '../partials/SpotSelection'
import { products } from '../Endpoints'
import { ReturnStepAfterVehicleStage } from '../partials/BackNavigation'

import { Divider } from '../../../../../Resources/Scripts/_Shared/elements/Divider'
import { RadioButtonListWithLabel } from '../../../../../Resources/Scripts/_Shared/elements/RadioButtonListWithLabel'
import { NumberSelectionWithLabel } from '../../../../../Resources/Scripts/_Shared/elements/NumberSelectionWithLabel'

import { ACTIONS } from './OnlineBooking'
import { shouldAccountBeUpdated } from '../../../../../Resources/Scripts/_Shared/helpers';
import { AccessDevicesList } from '../partials/AccessDevicesList';
import { SwitchWithLabel } from '../../../../../Resources/Scripts/_Shared/elements/SwitchWithLabel';
const emptyGuid = '00000000-0000-0000-0000-000000000000';

const initialState = (lang) => {
  return {
    form: {
      date: { date: new URLSearchParams(window.location.search).get('date'), error: null, required: true, elementType: 'date' },
      productSelection: { item: { Text: lang.chooseproduct, Id: emptyGuid, ZoneAndProduct: emptyGuid, initial: true }, required: true, elementType: 'select' },
      numberOfParkingSpots: { number: 1, elementType: 'number' },
      paymentSelection: { item: { id: emptyGuid, initial: true }, elementType: 'radio' },
      spotSelection: [{ item: { ParkingSpace: { Name: lang.choosespot, Uid: emptyGuid } }, initial: true }],
      accessDevices: [],
      accessDevicesOptional: []
    },
    pending: false,
  }
}

const reducer = (state, action) => {
  //console.log('reducer', action)
  switch (action.type) {
    case ACTIONS.OVERRIDE:
      return { ...state, form: { ...action.payload }};
    case ACTIONS.DATE:
      return { ...state, form: { ...state.form, date: action.payload }};
    case ACTIONS.PRODUCT:
      return {
        ...state, form: {
        ...state.form, productSelection: action.payload.value,
        accessDevices: state.form.productSelection.item.Type === action.payload.value.item.Type ? state.form.accessDevices : [],
        accessDevicesOptional: state.form.productSelection.item.Type === action.payload.value.item.Type ? state.form.accessDevicesOptional : [],
        spotSelection: [{ item: { ParkingSpace: { Name: action.payload.formLang.choosespot, Uid: emptyGuid } }, initial: true }]
      }};
    case ACTIONS.PAYMENT:
      return { ...state, form: { ...state.form, paymentSelection: action.payload }};
    case ACTIONS.SPOT_COUNT:
      return { ...state, form: { ...state.form, numberOfParkingSpots: action.payload.number ? action.payload : { ...state.form.numberOfParkingSpots, number: action.payload }}};
    case ACTIONS.SPOT_COUNT_INCREMENT:
      return { ...state, form: { ...state.form, numberOfParkingSpots: { number: (state.form.numberOfParkingSpots.number + 1), elementType: 'number' } } };
    case ACTIONS.SPOT_SELECTION:
      return { ...state, form: { ...state.form, spotSelection: action.payload } };
    case ACTIONS.ACCESS_DEVICES:
      return { ...state, form: { ...state.form, accessDevices: action.payload } };
      case ACTIONS.ACCESS_DEVICES_OPTIONAL:
      return { ...state, form: { ...state.form, accessDevicesOptional: action.payload } };
    case ACTIONS.SPOT_SELECTION_INCREMENT:
      return { ...state, form: { ...state.form, spotSelection: [...state.form.spotSelection, { item: { ParkingSpace: { Name: action.payload.choosespot, Uid: emptyGuid } }, initial: true }] } };
    case ACTIONS.PENDING:
      return { ...state, pending: action.payload };
    default:
      console.log('No reducer action specified.');
  }
}

const OnlineBookingLtp = () => {
  const { bookingFlowContext, setBookingFlowContext } = useContext(BookingFlowContext);
  const currentStepFormData = { ...bookingFlowContext.availableSteps.onlineBooking.formData } ?? {};

  const formLang = {
    ...bookingFlowContext.localization.global,
    ...bookingFlowContext.localization.onlineBooking,
    ...bookingFlowContext.localization.login,
    buttons: { ...bookingFlowContext.localization.global.buttons,
      ...bookingFlowContext.localization.onlineBooking.buttons,
      ...bookingFlowContext.localization.login.buttons},
    language: bookingFlowContext.localization.language,
  }
  const endDate = bookingFlowContext.episerver.LatestSalesorderStartDay ? moment(parseDate(bookingFlowContext.episerver.LatestSalesorderStartDay)).toDate() : moment().add(1, 'years').toDate();
  let id = null
  let urlParams = new URLSearchParams(window.location.search);

  const urlPid = urlParams.get('pid') ?? urlParams.get('pId');
  const contextPid = currentStepFormData.productSelection?.item?.ZoneAndProduct;
  const shouldRestoreFromContext = currentStepFormData.date && !urlParams.has('date') && (!urlPid || urlPid.endsWith('-') || urlPid === contextPid);
  const [stepState, dispatch] = useReducer(reducer, shouldRestoreFromContext ? { ...initialState(formLang), form: currentStepFormData } : initialState(formLang));
  const prevStepState = usePrevious(stepState);
  const isAccessDevice = !stepState.form.productSelection.item.initial ? stepState.form.productSelection.item.Family === 'Access Device' : false;
  const hasAccessDevice = !stepState.form.productSelection.item.initial && stepState.form.productSelection.item.AccessDevices ? stepState.form.productSelection.item.AccessDevices.length > 0 : false;
  const [productAvailability, productAvailabilityCount, paymentOptions, pendingAvailability, availableFrom] = useProductAvailability(isAccessDevice, stepState.form.productSelection.item, stepState.form.date.date, bookingFlowContext);

  let availableFromUrl = null;

  if (availableFrom && moment(availableFrom.from).isBefore(endDate)) {
    urlParams.set('date', parseDate(availableFrom.from, 'sv', 'L')), urlParams.set('pid', stepState.form.productSelection.item.ZoneAndProduct);
    availableFromUrl = window.location.origin + window.location.pathname + '?' + urlParams.toString();
    urlParams.delete('date');
  }

  let isReservedProduct = !stepState.form.productSelection.item.initial ? stepState.form.productSelection.item.Type === 'RESERVED' : null;

  const accessDeviceObject = useAccessDeviceAvailability(isAccessDevice, hasAccessDevice, stepState.form.productSelection.item, bookingFlowContext)

  const [accessDevices, pendingAccessDeviceAvailability, optionalAccessDevices, mandatoryADNotAvailable, mandatoryADAvailability] = isReservedProduct ? [stepState.form.productSelection.item.AccessDevices, false, stepState.form.productSelection.item.AccessDevices ? stepState.form.productSelection.item.AccessDevices.filter(device => !device.Bundled && !device.Required).length > 0 : null, false, null] : accessDeviceObject;
  const [maxAmount, setMaxAmount] = useState(null)
  const [spotADAmount, setSpotADAmount] = useState(null)
  const [spotADAmountPending, setSpotADAmountPending] = useState(false)
  const [spotADNotAvailable, setSpotADNotAvailable] = useState(false)
  const [isAfterPriceChange, setIsAfterPriceChange] = useState(false);
  id = stepState.form.productSelection.item.Id
  const locale = bookingFlowContext.country;
  const selectedLocale = locale === 'no' ? 'nb' : locale === 'se' ? 'sv' : locale;
  const [spotSelectionMade, setSpotSelectionMade] = useState(false);
  const [showAllSpots, setShowAllSpots] = useState(false);
  const [noNotAvailableSpots, setNoNotAvailableSpots] = useState(false);
  const [scrollEnabled, setScrollEnabled] = useState(null);
  const [fromUrl, setFromUrl] = useState(null);
  const useRadioButtonList = bookingFlowContext.episerver.useRadioButtonList === 'True';

  const isNotScrolledIntoView = (element) => {
    const rect = element.getBoundingClientRect();
    const elemTop = rect.top;
    const elemBottom = rect.bottom;
    const isVisible = (elemTop < window.innerHeight) && (elemBottom >= 0);
    return !isVisible;
  }

  useEffect(() => {
    if (id === bookingFlowContext.availableSteps.onlineBooking?.formData?.productSelection?.item?.Id) return;
    dispatch({ type: ACTIONS.SPOT_COUNT, payload: { number: 1, elementType: 'number' } })
  }, [id])

  useEffect(() => {
    if (stepState.form.date.error) return;
    const selectedProductId = stepState.form.productSelection.item.ZoneAndProduct;
    const storedDateIsInvalid = fromUrl !== null ? checkDate() : false;
    if (selectedProductId === emptyGuid) return;
    if (scrollEnabled) scrollNextOrDateIntoView(storedDateIsInvalid) 
    urlParams.set('pid', stepState.form.productSelection.item.ZoneAndProduct);
    history.replaceState({ activeStep: 0 }, document.title, window.location.origin + window.location.pathname + '?' + urlParams.toString());
  }, [id, stepState.form.date.date, fromUrl])

  useEffect(() => {
    if (hasAccessDevice && mandatoryADAvailability != null && productAvailabilityCount) {
      const max = mandatoryADAvailability < productAvailabilityCount ? { number: mandatoryADAvailability, type: 'AD' } : { number: productAvailabilityCount, type: 'product' }
      setMaxAmount(max)
    } else if (productAvailabilityCount) {
      setMaxAmount({ number: productAvailabilityCount, type: 'product' })
    }
  }, [id, mandatoryADAvailability, productAvailabilityCount])

  useEffect(() => {
    if (productAvailability && !productAvailability.some(spot => spot.ParkingSpaceStatus === 'NOT_AVAILABLE') || isAccessDevice) {
      setNoNotAvailableSpots(true);
      setShowAllSpots(false);
    }
  }, [productAvailability])

  useEffect(() => {
    setNoNotAvailableSpots(false);
    setShowAllSpots(false);
    setIsAfterPriceChange(false);

    if (stepState.form.date.date) {
      window.sessionStorage.setItem('selected_date', JSON.stringify(stepState.form.date));
    }

    if (!stepState.form.date.date || !id || !stepState.form?.productSelection?.item?.UpcomingPriceChange) return

    const selectedDate = parseDate(stepState.form.date.date);
    const startSellingDate = stepState.form.productSelection.item?.StartSellingDate ? moment(parseDate(stepState.form.productSelection.item?.StartSellingDate)).add(6, 'hours') : null;

    if (startSellingDate && moment(selectedDate).isBefore(startSellingDate, 'day')) {
      window.sessionStorage.removeItem('selected_date');
      dispatch({ type: ACTIONS.DATE, payload: { date: null, error: null, required: true, elementType: 'date' } });
    }
    setIsAfterPriceChange(moment(stepState.form?.productSelection?.item?.UpcomingPriceChange?.Validity.ValidFrom).isSameOrBefore(selectedDate, 'day'));
  }, [stepState.form.date, id])

  const checkDate = () => {
    let storedDateIsInvalid = false;
    const stored = window.sessionStorage.getItem('selected_date') ? JSON.parse(window.sessionStorage.getItem('selected_date')) : undefined;
    const flexDays = stepState.form.productSelection.item?.OnlineBookingFlexibleStartDate;

    if (stored && !urlParams.has('date')) {
      const addDays = (flexDays === null || flexDays === undefined) ? (locale === 'no' ? 3 : 7) : stepState.form.productSelection.item?.OnlineBookingFlexibleStartDate;
      const startDate = moment().add(addDays, 'days').toDate();
      const storedDate = parseDate(stored.date);
      storedDateIsInvalid = moment(storedDate).isBefore(startDate, 'day') || moment(storedDate).isAfter(endDate, 'day');
    }

    if (storedDateIsInvalid || stepState.form.date.error) {
      window.sessionStorage.removeItem('selected_date');
      if (!stepState.form.date.date && !stepState.form.date.error) dispatch({ type: ACTIONS.DATE, payload: { date: null, error: null, required: true, elementType: 'date' } });
    } else if (stored && ((!stepState.form.date.date && !stepState.form.date.error) || urlParams.has('date'))) {
      dispatch({ type: ACTIONS.DATE, payload: stored });
    }
    if (scrollEnabled === null) setTimeout(() => { setScrollEnabled(true) }, 1000);

    if (urlParams.has('date')) {
      urlParams.delete('date');
      history.replaceState({ activeStep: 0 }, document.title, window.location.origin + window.location.pathname + '?' + urlParams.toString());
    }
    return storedDateIsInvalid;
  }

  const scrollNextOrDateIntoView = (storedDateIsInvalid) => {
    setTimeout(() => {
      if (document.querySelector('.DayPickerInput-OverlayWrapper')) return;
      if (stepState.form.date.date && !storedDateIsInvalid) {
        setTimeout(() => {
          const nextButton = document.querySelector('.single-step__button--next');
          isNotScrolledIntoView(nextButton) && nextButton.scrollIntoView({ behavior: "smooth", block: "end" })
        }, 200);
      } else if (scrollEnabled && (storedDateIsInvalid || !stepState.form.date.date)) {
        const dateSelection = document.querySelector('.single-step__section--date .single-step__section__label');
        isNotScrolledIntoView(dateSelection) && dateSelection.scrollIntoView({ behavior: "smooth" })
      }
    }, 200);
  }

  const onlineBookingHandler = async () => {
    if (!stepState.form.date.date) {
      scrollNextOrDateIntoView(true);
      dispatch({ type: ACTIONS.DATE, payload: { ...stepState.form.date, error: true } });
      return;
    }
    const loggedIn = bookingFlowContext.episerver.loggedIn;
    const switchAccountUrl = bookingFlowContext.episerver.switchAccountUrl;
    const activeProfile = !!bookingFlowContext.episerver.activeProfile;
    const hasMoreThanOneProfile = bookingFlowContext.episerver.hasMoreThanOneProfile;

    const spotSelection = stepState.form.spotSelection;
    urlParams = urlParams.toString().length > 0 ? '?' + urlParams.toString() + '&' : '?';

    let [updatedForm, formValidationError, cleanFormData] = validateFormObject(stepState.form, formLang.errorMessages);
    if (formValidationError) {
      dispatch({ type: ACTIONS.OVERRIDE, payload: updatedForm })
      return;
    }

    let activeStep = null;
    let user = null;

    if (bookingFlowContext.episerver.loggedIn && (!hasMoreThanOneProfile || activeProfile)) {
      user = {
        email: bookingFlowContext.episerver.emailAddress,
        givenName: bookingFlowContext.episerver.given_name,
        familyName: bookingFlowContext.episerver.family_name,
      }
      if (bookingFlowContext.episerver.hasWebticketAccount && !shouldAccountBeUpdated(bookingFlowContext)) {
        // If permit Usage type is SKIDATA, jump over 'Add vehicle' step (CPS-24723) 
        const isSkidata = stepState.form.productSelection?.item?.PermitUsageType?.toLowerCase() === "skidata";
        
        let newActiveStep = bookingFlowContext.availableSteps.addVehicle.index;

        if (isSkidata) {
            newActiveStep = ReturnStepAfterVehicleStage(bookingFlowContext);
        }
            
        activeStep = newActiveStep;
      } else {
        activeStep = bookingFlowContext.availableSteps.createAccount.index;
      }
    } else if (loggedIn && hasMoreThanOneProfile && !activeProfile) {
      activeStep = bookingFlowContext.availableSteps.onlineBooking.index;
      window.location.href = switchAccountUrl + encodeURIComponent(urlParams + 'l=lgn');
    } else {
      activeStep = bookingFlowContext.availableSteps.onlineBooking.index;
      window.location.href = bookingFlowContext.episerver.loginUrl + encodeURIComponent(urlParams + 'l=lgn');
    }

    const previousBooking = bookingFlowContext.availableSteps.onlineBooking.formData;
    const numberOfParkingSpots = stepState.form.numberOfParkingSpots.number || stepState.form.spotSelection.length;
    const changedSpots = isReservedProduct && spotSelection && previousBooking?.spotSelection && (spotSelection.length != previousBooking.spotSelection?.length || (spotSelection.some(item => !previousBooking.spotSelection?.some(prevItem => prevItem.item.ParkingSpace.Uid === item.item.ParkingSpace.Uid))));

    const update = previousBooking ? previousBooking.productSelection.item.Id != id || previousBooking.numberOfParkingSpots.number != numberOfParkingSpots || previousBooking.date.date != stepState.form.date.date || previousBooking.paymentSelection.item.id != stepState.form.paymentSelection.item.id || changedSpots : true;

    setBookingFlowContext({
      ...bookingFlowContext,
      user,
      activeStep,
      availableSteps: {
        ...bookingFlowContext.availableSteps,
        onlineBooking: {
          ...bookingFlowContext.availableSteps.onlineBooking,
          formData: {
            ...cleanFormData
          },
          update
        },
        addVehicle: {
          ...bookingFlowContext.availableSteps.addVehicle,
          formData: bookingFlowContext.availableSteps.onlineBooking.formData?.productSelection.item.Type === stepState.form.productSelection.item.Type ? bookingFlowContext.availableSteps.addVehicle.formData : null
        }
      }
    })
  };

  useEffect(() => {
    const loggedIn = bookingFlowContext.episerver.loggedIn;
    const switchAccountUrl = bookingFlowContext.episerver.switchAccountUrl;
    const activeProfile = !!bookingFlowContext.episerver.activeProfile;
    const hasMoreThanOneProfile = bookingFlowContext.episerver.hasMoreThanOneProfile;
    const alreadyLoggedIn = !!bookingFlowContext.user;
    const productNotSelected = stepState.form.productSelection.item.initial;
    const urlParams = new URLSearchParams(window.location.search);
    const loginType = urlParams.get('L') ?? urlParams.get('l');
    history.replaceState({ activeStep: 0 }, document.title);

    if (alreadyLoggedIn || productNotSelected || (!loggedIn || !loginType) && !hasMoreThanOneProfile) {
      return;
    }

    if (loginType === 'lgn' && loggedIn && hasMoreThanOneProfile && !activeProfile) {
      window.location.href = switchAccountUrl + encodeURIComponent('?' + urlParams);
      return;
    }

    if (bookingFlowContext.episerver.hasWebticketAccount && !shouldAccountBeUpdated(bookingFlowContext) && (!hasMoreThanOneProfile || activeProfile)) {
        // If permit Usage type is SKIDATA, jump over 'Add vehicle' step (CPS-24723) 
        const isSkidata = stepState.form.productSelection?.item?.PermitUsageType?.toLowerCase() === "skidata";
        
        let newActiveStep = bookingFlowContext.availableSteps.addVehicle.index;

        if (isSkidata) {
            newActiveStep = ReturnStepAfterVehicleStage(bookingFlowContext);
        }

        setBookingFlowContext({
        ...bookingFlowContext,
        activeStep: newActiveStep,
        user: {
          email: bookingFlowContext.episerver.emailAddress,
          givenName: bookingFlowContext.episerver.given_name,
          familyName: bookingFlowContext.episerver.family_name,
        }
      })
    }
    else if (loginType === 'gst') {
        setBookingFlowContext({ ...bookingFlowContext, activeStep: bookingFlowContext.availableSteps.createAccount.index, guest: true })
    }
    else if (loginType === 'lgn'){
      setBookingFlowContext({ ...bookingFlowContext, activeStep: bookingFlowContext.availableSteps.createAccount.index, guest: false })
    }


  }, [bookingFlowContext.episerver.activeProfile])

  useEffect(() => {
    dispatch({ type: ACTIONS.ACCESS_DEVICES, payload: [] });
  }, [hasAccessDevice])

  const addSelectedAccessDevices = (selectedAccessDevices) => {
    dispatch({ type: ACTIONS.ACCESS_DEVICES, payload: selectedAccessDevices })
  }

  const handleReservedSelectedAccessDevices = (selectedAccessDevices, currentSpots) => {
    const changedAD = selectedAccessDevices.filter(a => a.changed)
    const selectedSpots = currentSpots ? currentSpots.map(spot => spot.item.ParkingSpace.Name) : stepState.form.spotSelection.map(spot => spot.item.ParkingSpace.Name)
    let updatedState
    if (currentSpots) {
      if (selectedAccessDevices.some(a => a.spotName.some(b => !selectedSpots.includes(b)))) {
        updatedState = selectedAccessDevices.map(a => { return a.spotName.some(b => !selectedSpots.includes(b)) ? { ...a, Quantity: a.Quantity - 1, spotName: a.spotName.filter(b => selectedSpots.includes(b)) } : a }).filter(a => a.Quantity !== 0)
      }
    } else if (stepState?.form?.accessDevicesOptional?.length && changedAD.length) {
      const adToIncrease = changedAD.filter(a => stepState.form.accessDevicesOptional.some(b => { return a.id === b.id && a.selected }))[0]
      const adToDecrease = changedAD.filter(a => stepState.form.accessDevicesOptional.some(b => { return a.id === b.id && !a.selected }))[0]
      if (adToIncrease) {
        if (stepState.form.accessDevicesOptional.some(a => a.spotName.includes(changedAD[0].spotName[0]))) return
        updatedState = stepState.form.accessDevicesOptional.map(a => { return a.id === adToIncrease.id ? { ...a, Quantity: a.Quantity + +changedAD.map(b => b.id === a.id && b.Quantity), spotName: [...a.spotName, changedAD.map(b => b.id === a.id && b.spotName[0])[0]] } : a })
      } else if (adToDecrease) {
        updatedState = stepState.form.accessDevicesOptional.map(a => { return a.id === adToDecrease.id ? { ...a, Quantity: a.Quantity - +changedAD.map(b => b.id === a.id && b.Quantity), spotName: a.spotName.filter(a => a !== adToDecrease.spotName[0]) } : a }).filter(a => a.Quantity !== 0)
      } else {
        updatedState = [...stepState.form.accessDevicesOptional, ...changedAD]
      }
    } else if (selectedAccessDevices.some(a => a.selected)) {
      updatedState = selectedAccessDevices.filter(device => device.selected)
    }
    if (updatedState) dispatch({ type: ACTIONS.ACCESS_DEVICES_OPTIONAL, payload: updatedState })
  }

  const checkSpotADAvailability = async (spotNames) => {
    const spotName = spotNames[spotNames.length - 1]
    const accessDevicesParams = [];

    accessDevices.filter(device => {
      accessDevicesParams.push({
        ProductId: device.ProductDetails?.ProductId,
        Spot: spotName
      })
    })
    const params = {
      accessDevices: accessDevicesParams
    }

    return await products.getAccessDeviceAvailability(params);
  }

  const checkSpotADAmount = async (validSelectionList) => {
    setSpotADAmountPending(true)
    const mandatoryAD = accessDevices?.some(ad => ad.Required || ad.Bundled)
    let choosenSpots = []
    let leastRequiredOrBundledADAvailable

    if (!spotADAmount && validSelectionList.length > 1) {
      const spotNames = validSelectionList.map(item => { return item.item.ParkingSpace.Name })
      choosenSpots = spotNames.map(spotName => { return { spotName, availability: null } })
      setSpotADAmount(choosenSpots)

      const response = await checkSpotADAvailability(spotNames)

      if (response.status === 200) {
        leastRequiredOrBundledADAvailable = calculateLeastRequiredOrBundledADAvailable(accessDevices, response.body?.AccessDevices)
        choosenSpots = spotNames.map((spotName, i) => { return { spotName, availability: response.body?.AccessDevices } })
        if (leastRequiredOrBundledADAvailable) setMaxAmount({ number: leastRequiredOrBundledADAvailable, type: 'AD' })
      } else {
        console.log("Access device availability check error")
        choosenSpots = spotNames.map((spotName, i) => { return { spotName, availability: null, productId: null } })
        if (mandatoryAD) setMaxAmount({ number: 0, type: 'AD' })
      }
    }
    else {
      const spotName = validSelectionList[validSelectionList.length - 1].item.ParkingSpace.Name
      choosenSpots = spotADAmount ?? [{ spotName, availability: null }]

      if (choosenSpots.length === validSelectionList.length)
        choosenSpots[validSelectionList.length - 1] = { spotName, availability: null }
      else
        choosenSpots.push({ name: spotName, availability: null })

      setSpotADAmount(choosenSpots)
      const response = await checkSpotADAvailability([spotName])

      if (response.status === 200) {
        leastRequiredOrBundledADAvailable = calculateLeastRequiredOrBundledADAvailable(accessDevices, response.body?.AccessDevices)
        choosenSpots[choosenSpots.length - 1] = { spotName, availability: response.body?.AccessDevices }
        if (leastRequiredOrBundledADAvailable) setMaxAmount({ number: leastRequiredOrBundledADAvailable, type: 'AD' })
      } else {
        console.log("Access device availability check error")
        choosenSpots[choosenSpots.length - 1] = { spotName, availability: null }
        if (mandatoryAD) setMaxAmount({ number: 0, type: 'AD' })
      }
    }
    setSpotADAmount(choosenSpots)
    setTimeout(() => {
      setSpotADNotAvailable(leastRequiredOrBundledADAvailable < 1)
      setSpotADAmountPending(false)
    }, 500);
  }

  useEffect(() => {
    if (!isReservedProduct && !hasAccessDevice) return;
    handleSpotSelectionError();
  }, [spotADNotAvailable, stepState.form.spotSelection.length])

  const handleSpotSelectionError = () => {
    if (spotADNotAvailable === stepState.form.spotSelection[stepState.form.spotSelection.length - 1].error) return;
    const selectionList = stepState.form.spotSelection;
    const groupedList = groupBy(selectionList, item => item.item.ParkingSpace.Uid);
    const validSelectionList = selectionList.map((item, i) => { return { ...item, error: groupedList[item.item.ParkingSpace.Uid].length > 1 || (i === selectionList.length - 1 && spotADNotAvailable) ? true : false } });
    const lastInList = validSelectionList[validSelectionList.length - 1]
    if (lastInList.item.ParkingSpace.Uid !== emptyGuid && !lastInList.error && (!lastInList.item.ParkingSpace.Name || (spotADAmount && lastInList.item?.ParkingSpace?.Name !== spotADAmount[spotADAmount.length - 1]?.spotName))) {
      checkSpotADAmount(validSelectionList);
    }
    dispatch({ type: ACTIONS.SPOT_SELECTION, payload: validSelectionList });
  }

  const addSpotSelectionHandler = async (selection, selectionIndex) => {
    if (isReservedProduct && hasAccessDevice) setSpotADNotAvailable(false);
    const currentSpotADSelection = spotADAmount
    const selectionList = stepState.form.spotSelection.map((item, index) => index === selectionIndex ? { ...selection } : item);
    const groupedList = groupBy(selectionList, item => item.item.ParkingSpace.Uid);
    const validSelectionList = selectionList.map((item) => { return { ...item, error: groupedList[item.item.ParkingSpace.Uid].length > 1 ? true : false, initial: false } });
    if (spotADAmount?.length === validSelectionList.length) validSelectionList.forEach((item, i) => {
      if (groupedList[item.item.ParkingSpace.Uid].length > 1 && i === spotADAmount?.length - 1) {
        currentSpotADSelection.splice(selectionIndex, 1);
        setSpotADAmount(currentSpotADSelection);
      }
    });
    dispatch({ type: ACTIONS.SPOT_SELECTION, payload: validSelectionList })

    if (selection.item.ParkingSpace && selection.item.ParkingSpace.Uid !== emptyGuid && validSelectionList.filter(item => item.error).length === 0) {
      if (hasAccessDevice && accessDevices && accessDevices.length) checkSpotADAmount(validSelectionList);
      setSpotSelectionMade(true);
    }
    else {
      setSpotSelectionMade(false);
    }
  }

  const removeSpotSelectionHandler = (selectionIndex) => {
    const currentSpotSelections = stepState.form.spotSelection;
    const currentSpotADSelection = spotADAmount;
    if (selectionIndex > -1) {
      currentSpotSelections.splice(selectionIndex, 1);
      if (currentSpotADSelection) {
        currentSpotADSelection.splice(selectionIndex, 1)
        setSpotADAmount(currentSpotADSelection)
        handleSpotSelectionError()
        if (selectionIndex === stepState.form.spotSelection.length) {
          setMaxAmount({ number: +Math.min(currentSpotADSelection[selectionIndex - 1].availability.map(device => { return device.Availability })), type: 'AD' })
          setSpotADNotAvailable(null);
        }
      }
    }
    if (currentSpotADSelection) setSpotADAmount(currentSpotADSelection)
    const groupedList = groupBy(currentSpotSelections, item => item.item.ParkingSpace.Uid);
    const validSelectionList = currentSpotSelections.map((item) => { return { ...item, error: (item.item.ParkingSpace.Uid !== emptyGuid && groupedList[item.item.ParkingSpace.Uid].length > 1) ? true : false } });

    if (validSelectionList.filter(spot => spot.item.ParkingSpace.Uid === emptyGuid || spot.error).length > 0)
      setSpotSelectionMade(false);
    else {
      setSpotSelectionMade(true);
    }
    dispatch({ type: ACTIONS.SPOT_SELECTION, payload: validSelectionList })
  }

  useEffect(() => {
    if (!productAvailability || productAvailability.length === 0 || stepState.form.spotSelection.length === 0 || stepState.form.productSelection.item.Type !== 'RESERVED')
      return;
    const filteredItems = stepState.form.spotSelection.map((item) => {
      if (productAvailability.filter(newItem => newItem.ParkingSpace.Uid === item.item.ParkingSpace.Uid).length > 0)
        return { ...item, initial: false };
      else {
        setSpotADAmount(null);
        setSpotADNotAvailable(null);
        return { item: { ParkingSpace: { Name: formLang.choosespot, Uid: emptyGuid } }, initial: true }
      }
    })
    hasAccessDevice && accessDevices && accessDevices.length && !filteredItems.slice(0, productAvailability.length ?? 1)[0].initial && checkSpotADAmount(filteredItems.slice(0, productAvailability.length ?? 1))
    dispatch({ type: ACTIONS.SPOT_SELECTION, payload: filteredItems.slice(0, productAvailability.length ?? 1) });
    setSpotSelectionMade(filteredItems.filter(item => !item.initial).length > 0);
  }, [productAvailability])

  useEffect(() => {
    if (!stepState || !prevStepState)
      return;

    if (stepState.form.spotSelection.length !== prevStepState.form.spotSelection.length)
      removeSpotSelectionHandler(-1);

  }, [stepState.form.spotSelection])
  
  const getNewPrices = (options, upcomingPriceChange) => {
    return options.map(option => {
      for (const [key, value] of Object.entries(upcomingPriceChange)) {
        if (key.toLowerCase().includes(option.value.toLowerCase().replace('_','').substring(0,6)) && value) return { ...option, optionPrice: value }
      }
      return option
    })
  }

  return (
    <Fragment>
      <DateAndProductSelection
        bookingFlowContext={bookingFlowContext}
        formLang={formLang}
        setFromUrl={setFromUrl}
        pending={stepState.pending || fromUrl === null}
        date={stepState.form.date}
        productSelection={stepState.form.productSelection}
        useRadioButtonList={useRadioButtonList}
        setPending={(value) => dispatch({type: ACTIONS.PENDING, payload: value })}
        setDate={(value) => dispatch({type: ACTIONS.DATE, payload: value })}
        setProductSelection={(value) => { setSpotADAmount(null); dispatch({ type: ACTIONS.PRODUCT, payload: { value, formLang } }) }}
        isAfterPriceChange={isAfterPriceChange}
        scrollEnabled={scrollEnabled}
        setScrollEnabled={setScrollEnabled}
        dateDisabled={stepState.form.date.date && !stepState.form.productSelection.item.initial && (!pendingAvailability && ((productAvailabilityCount === 0 && !availableFrom) || (isReservedProduct ? (productAvailability?.length === 0 && !availableFrom) : false)))}
        availableFrom={availableFrom}
      />

      {stepState.form.date.date && !stepState.form.productSelection.item.initial && (!pendingAvailability && (productAvailabilityCount === 0 || (isReservedProduct ? productAvailability?.length === 0 : false))) &&
        <Fragment>
          <Error text={availableFrom ? formLang.availableparkingspotsinfodate.replace('{0}', parseDate(availableFrom.until, selectedLocale, 'L')) + (availableFromUrl ? ' ' + formLang.availableparkingspotsinfolink.replace('{1}', availableFromUrl) : '') : formLang.availableparkingspotsinfonolink} />
        </Fragment>
      }

      {!stepState.form.productSelection.item.initial && stepState.form.productSelection.item.UpcomingPriceChange &&
        stepState.form.productSelection.item.MonthlyPrice !== stepState.form.productSelection.item.UpcomingPriceChange.MonthlyPrice &&
        <Fragment>
        <Error text={[`${formLang.pricechangedate} ${parseDate(stepState.form.productSelection.item.UpcomingPriceChange.Validity.ValidFrom, selectedLocale, 'L')}.`, stepState.form.productSelection.item.UpcomingPriceChange.MonthlyPrice ? ` ${formLang.pricechangeprice} ${stepState.form.productSelection.item.UpcomingPriceChange.MonthlyPrice} ${stepState.form.productSelection.item.Currency}.` : ''].join('')} />
        </Fragment>
      }

      {(!stepState.form.productSelection.item.initial && (productAvailabilityCount && productAvailabilityCount > 0 && productAvailability?.length > 0) || pendingAvailability) && isReservedProduct && (stepState.form.spotSelection?.length > 0) &&
        <Fragment>
          <SpotSelection
            locale={selectedLocale}
            productAvailability={productAvailability && productAvailability.filter(spot => (showAllSpots || isAccessDevice) ? spot : spot.ParkingSpaceStatus === 'AVAILABLE')}
            productAvailabilityCount={productAvailabilityCount}
            maxAmount={maxAmount ?? 1}
            spotSelection={stepState.form.spotSelection}
            formLang={formLang}
            pending={stepState.pending || spotADAmountPending}
            pendingAvailability={pendingAvailability}
            addSpotEvent={addSpotSelectionHandler}
            removeSpotEvent={removeSpotSelectionHandler}
            selectedDate={stepState.form.date}
            incrementEvent={() => dispatch({ type: ACTIONS.SPOT_SELECTION_INCREMENT, payload: formLang })}>
            {hasAccessDevice && !pendingAccessDeviceAvailability && !pendingAvailability && spotADAmount && spotADAmount.map((spotAD, i) => { return spotAD.spotName && <AccessDevicesList key={spotAD.spotName} spotADAmount={spotAD} formLang={formLang} availabilityPending={i === spotADAmount.length-1 ? spotADAmountPending : false} addSelectedAccessDevices={addSelectedAccessDevices} reserved={true} numberOfParkingSpots={stepState.form.spotSelection.length} nextToProduct={spotADAmount} accessDevices={accessDevices} currency={stepState.form.productSelection.item.Currency} /> })}
          </SpotSelection>
        <SwitchWithLabel disabled={pendingAvailability || noNotAvailableSpots} value={showAllSpots} setValue={setShowAllSpots} error={noNotAvailableSpots} text={formLang.futureAvailability} />
        </Fragment>
      }
      {!stepState.form.productSelection.item.initial && ((productAvailabilityCount && productAvailabilityCount > 0) || pendingAvailability) && (isReservedProduct !== null && isReservedProduct === false) &&
        <Fragment>
          <Divider />
          <NumberSelectionWithLabel text={formLang.numberOfParkingSpots} exceededText={maxAmount?.type === 'product' ? formLang.exceededMaximumAmountInfo : formLang.accessDevice.exceededMaximumAmountInfo} value={stepState.form.numberOfParkingSpots} setValue={(value) => dispatch({ type: ACTIONS.SPOT_COUNT, payload: value })} defaultMinValue={1} defaultMaxValue={maxAmount?.number ?? 1} disabled={(hasAccessDevice && mandatoryADNotAvailable) || pendingAccessDeviceAvailability || stepState.pending || pendingAvailability || productAvailabilityCount === 0}>
            <div className="single-step__section">
              {productAvailabilityCount > 0 && stepState.form.numberOfParkingSpots.number === productAvailabilityCount && <p className="italic">{formLang.numberOfParkingSpotsInfo}</p>}
            </div>
          </NumberSelectionWithLabel>
          {hasAccessDevice && !pendingAccessDeviceAvailability && <AccessDevicesList formLang={formLang} addSelectedAccessDevices={addSelectedAccessDevices} numberOfParkingSpots={stepState.form.numberOfParkingSpots.number} nextToProduct={true} accessDevices={accessDevices} currency={stepState.form.productSelection.item.Currency} />}
        </Fragment>
      }

      {!stepState.form.productSelection.item.initial && paymentOptions.length > 0 && !(productAvailabilityCount === 0 || (isReservedProduct ? productAvailability?.length === 0 : false)) &&
        <Fragment>
          <Divider />
        <RadioButtonListWithLabel text={formLang.paymentSelection} options={isAfterPriceChange && stepState.form.productSelection?.item?.UpcomingPriceChange ? getNewPrices(paymentOptions, stepState.form.productSelection?.item?.UpcomingPriceChange) : paymentOptions} priceTimes={isReservedProduct ? stepState.form.spotSelection.length : stepState.form.numberOfParkingSpots.number} locale={bookingFlowContext.country} value={stepState.form.paymentSelection} setValue={(value) => dispatch({type: ACTIONS.PAYMENT, payload: value })}
          disabled={stepState.pending || pendingAvailability || productAvailabilityCount === 0 || (isReservedProduct ? productAvailability?.length === 0 : false)} />
        </Fragment>
      }

      {!stepState.form.productSelection.item.initial && stepState.form.productSelection.item.ExtraFee &&
        <Fragment>
          <Divider />
          <HeadingAndText heading={stepState.form.productSelection.item.ExtraFee.Heading} text={stepState.form.productSelection.item.ExtraFee.Text} />
        </Fragment>
      }
     
      {!stepState.form.productSelection.item.initial && hasAccessDevice && !pendingAccessDeviceAvailability && !pendingAvailability && !stepState.pending && (optionalAccessDevices && (productAvailabilityCount && productAvailabilityCount > 0)) && accessDevices?.length > 0 && (isReservedProduct ? !stepState.form.spotSelection[0].initial && optionalAccessDevices : true) &&
        <Fragment>
        <Divider />
          <div className="access-device__wrapper">
            {isReservedProduct && spotADAmount?.length ?
            spotADAmount.map((spotAD, i) => { return spotAD.spotName && <AccessDevicesList key={spotAD.spotName} spotADAmount={spotAD} headings={(i === 0)} formLang={formLang} availabilityPending={i === spotADAmount.length - 1 ? spotADAmountPending : false} addSelectedAccessDevices={handleReservedSelectedAccessDevices} reserved={true} numberOfParkingSpots={stepState.form.spotSelection.length} optional={optionalAccessDevices} accessDevices={accessDevices} currency={stepState.form.productSelection.item.Currency} /> })
            : !isReservedProduct && <AccessDevicesList formLang={formLang} optional={true} addSelectedAccessDevices={(payload) => dispatch({ type: ACTIONS.ACCESS_DEVICES_OPTIONAL, payload })} accessDevices={accessDevices} currency={stepState.form.productSelection.item.Currency} numberOfParkingSpots={isReservedProduct ? stepState.form.spotSelection.length : stepState.form.numberOfParkingSpots.number} />}
          </div>
        </Fragment>
      }

      <Divider />

      {!bookingFlowContext.episerver.loggedIn && <h2 className="smallmargin">{formLang.heading}</h2>}
      {!bookingFlowContext.episerver.loggedIn && formLang.loginPreamble && <p className="maxwidth margin center">{formLang.loginPreamble}</p>}
      <button className="single-step__button single-step__button--next" data-booking-flow-step="ltp-selectproduct" onClick={onlineBookingHandler} disabled={stepState.form.productSelection.item.initial || (stepState.form.date.date && (stepState.form.date.error || (isReservedProduct && productAvailability?.length === 0) || stepState.pending || pendingAvailability || productAvailabilityCount === null || productAvailabilityCount === 0 || (isReservedProduct !== null && isReservedProduct === true && !spotSelectionMade) || spotADAmountPending || pendingAccessDeviceAvailability || (hasAccessDevice && ((!isReservedProduct && mandatoryADNotAvailable) || (isReservedProduct && spotADNotAvailable && productAvailability?.length > 0)))))}>{bookingFlowContext.episerver.loggedIn ? formLang.buttons.nextStep : formLang.buttons.loginOrCreate}</button>
      {stepState.form.date.date && hasAccessDevice && !pendingAccessDeviceAvailability && !pendingAvailability && !spotADAmountPending && !stepState.pending && ((!isReservedProduct && mandatoryADNotAvailable && productAvailabilityCount > 0) || (isReservedProduct && spotADNotAvailable && productAvailability?.length > 0)) && <div className="single-step__section access-device-error">
        <div className="validation-error validation-error--inline">
          <p>{formLang.accessDevice.mandatoryNotAvailable}</p>
        </div>
      </div>}
    </Fragment>
  )
}

export default OnlineBookingLtp;
